import React, { useEffect, useRef } from 'react';
import { TURNSTILE_SITE_KEY } from '../lib/firebaseConfig';

/**
 * TurnstileWidget — renders a Cloudflare Turnstile bot-check and hands the
 * resulting token back via onVerify. Used by the sign-up waitlist
 * (WaitlistScreen) so bots can't spam the waitlist with fake emails.
 *
 * Walter 2026-06-02. The Cloudflare script is loaded lazily, once, the
 * first time a widget mounts (the waitlist is a rare path — no reason to
 * pull the script on every page load). The SITE key is public; the SECRET
 * lives only in the joinWaitlist Cloud Function.
 *
 * onVerify(token) fires when the challenge passes (token is a one-time
 * string the server verifies); onVerify(null) fires if the token expires.
 * onError fires if the script/widget fails to load — the caller can then
 * fall back gracefully (e.g. show a "try again" note).
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise = null;

function loadTurnstileScript() {
  if (typeof window !== 'undefined' && window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => { scriptPromise = null; reject(new Error('turnstile script failed to load')); };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function TurnstileWidget({ onVerify, onError }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  // Keep the latest callbacks in a ref so the render effect can run once
  // (re-rendering the widget on every parent re-render would reset the
  // challenge and is visually jarring).
  const cbRef = useRef({ onVerify, onError });
  cbRef.current = { onVerify, onError };

  useEffect(() => {
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => cbRef.current.onVerify?.(token),
          'error-callback': () => cbRef.current.onError?.(),
          'expired-callback': () => cbRef.current.onVerify?.(null),
          theme: 'auto',
        });
      })
      .catch(() => cbRef.current.onError?.());
    return () => {
      cancelled = true;
      try {
        if (window.turnstile && widgetIdRef.current != null) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch { /* widget already gone */ }
    };
  }, []);

  return <div ref={containerRef} style={{ minHeight: 65 }} />;
}
